// src/pages/Accounting/JournalEntries/JournalEntryForm.jsx
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useReactToPrint } from 'react-to-print';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import {
  Box, Paper, TextField, Button, Typography, IconButton, Tooltip, Alert, CircularProgress, MenuItem,
} from '@mui/material';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import TagIcon from '@mui/icons-material/Tag';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import BalanceIcon from '@mui/icons-material/Balance';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

import axios from '../../../axios';
import chartOfAccountsApi from '../../../api/chartOfAccountsApi';
import AccountSelect from '../../../components/AccountSelect';
import { ReportPage, fieldSx, fmtMoney, REPORT_COLORS } from '../../../components/Reports/ReportKit';
import ImportJournalEntryDialog from './ImportJournalEntryDialog';
import JournalEntryPrint from './JournalEntryPrint';
import { getEntryTypes, entryTypeLabel, ENTRY_STATUS, isSystemEntry, apiError } from './journalEntryTypes';

const today = () => dayjs().format('YYYY-MM-DD');
const toDate = (v) => (v ? String(v).split('T')[0] : today());
const num = (v) => Number(v) || 0;
const round2 = (v) => Math.round(num(v) * 100) / 100;
let lineSeq = 0;
const newLine = (p = {}) => ({ id: `n${Date.now()}_${lineSeq++}`, accountId: null, description: '', debit: '', credit: '', ...p });

const BORDER = '#e5e7eb';
const ACCENT = REPORT_COLORS.primary.main;
const amountSx = { ...fieldSx, '& input': { direction: 'ltr', textAlign: 'right', fontWeight: 700 } };

function Section({ title, icon, children, action, noPad }) {
  return (
    <Paper elevation={0} sx={{ border: `1px solid ${BORDER}`, borderRadius: '14px', overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: { xs: 1.5, sm: 2 }, pt: 1.5, pb: noPad ? 1.5 : 0, flexWrap: 'wrap' }}>
        <Box sx={{
          width: 30, height: 30, borderRadius: '9px', display: 'grid', placeItems: 'center',
          bgcolor: REPORT_COLORS.primary.soft, color: ACCENT, '& svg': { fontSize: 18 },
        }}>{icon}</Box>
        <Typography sx={{ fontWeight: 800, fontSize: 15, flex: 1 }}>{title}</Typography>
        {action}
      </Box>
      <Box sx={{ p: noPad ? 0 : { xs: 1.5, sm: 2 } }}>{children}</Box>
    </Paper>
  );
}

export default function JournalEntryForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const [searchParams] = useSearchParams();

  const companyId = parseInt(localStorage.getItem('companyId'), 10);
  const branchId = parseInt(localStorage.getItem('branchId'), 10);
  const routeView = location.pathname.includes('/view');
  const duplicate = location.state?.duplicate;
  const initialType = duplicate?.entryType || parseInt(searchParams.get('type'), 10) || 1;

  const entryTypes = useMemo(() => getEntryTypes(t), [t]);

  const [form, setForm] = useState(() => ({
    date: today(),
    referenceNumber: '',
    entryType: initialType,
    description: duplicate?.description || '',
    branchId,
    entryNumber: '',
    status: null,
  }));
  const [lines, setLines] = useState(() =>
    duplicate?.lines?.length
      ? duplicate.lines.map(l => newLine({ accountId: l.accountId, description: l.description, debit: l.debit, credit: l.credit }))
      : [newLine(), newLine()]);
  const [loading, setLoading] = useState(!!id);
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [accountMap, setAccountMap] = useState({});
  const [companyInfo, setCompanyInfo] = useState(null);
  const [showErrors, setShowErrors] = useState(false);

  // قيد مرحّل أو آلي يُفتح للعرض فقط حتى لو جاء من رابط التعديل
  const locked = !!id && (form.status != null && form.status !== 1 || isSystemEntry(form.entryType));
  const isView = routeView || locked;
  const isEdit = !!id && !isView;

  // ── تحميل الحسابات وبيانات الشركة (للطباعة) ──
  useEffect(() => {
    chartOfAccountsApi.getAll()
      .then(res => {
        const map = {};
        (res.data || []).forEach(a => { map[a.id] = `${a.code} - ${a.nameAr}`; });
        setAccountMap(map);
      })
      .catch(err => console.error('Error fetching accounts:', err));
    if (companyId) {
      axios.get(`/CompanyProfile/public/${companyId}`).then(r => setCompanyInfo(r.data)).catch(() => {});
    }
  }, [companyId]);

  const fetchNextNumber = useCallback(async (type) => {
    try {
      const { data } = await axios.get('/JournalEntries/next-number', { params: { branchId, entryType: type } });
      setForm(f => ({ ...f, entryNumber: data?.nextNumber ?? '' }));
    } catch (err) {
      console.error('Failed to fetch next number', err);
    }
  }, [branchId]);

  // ── تحميل القيد أو الرقم التالي ──
  useEffect(() => {
    let alive = true;
    if (!id) {
      fetchNextNumber(initialType);
      return () => { alive = false; };
    }
    setLoading(true);
    axios.get(`/JournalEntries/${id}`)
      .then(({ data: e }) => {
        if (!alive) return;
        setForm({
          date: toDate(e.date),
          referenceNumber: e.referenceNumber || '',
          entryType: e.entryType,
          description: e.description || '',
          branchId: e.branchId ?? branchId,
          entryNumber: e.entryNumber,
          status: e.status ?? null,
        });
        setLines((e.lines || []).map((l, i) => ({
          id: l.id ?? `l${i}`,
          accountId: l.accountId,
          description: l.description || '',
          debit: num(l.debit) || '',
          credit: num(l.credit) || '',
        })));
      })
      .catch(err => alive && setLoadError(apiError(err, 'تعذّر تحميل القيد')))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [id]);

  // عند النسخ من قيد مفتوح إلى /new قد يُعاد استخدام نفس المكوّن — نعيد تهيئة الحالة
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) { firstRender.current = false; return; }
    if (id) return;
    const dup = location.state?.duplicate;
    const type = dup?.entryType || parseInt(searchParams.get('type'), 10) || 1;
    setForm({ date: today(), referenceNumber: '', entryType: type, description: dup?.description || '', branchId, entryNumber: '', status: null });
    setLines(dup?.lines?.length
      ? dup.lines.map(l => newLine({ accountId: l.accountId, description: l.description, debit: l.debit, credit: l.credit }))
      : [newLine(), newLine()]);
    setLoadError('');
    setShowErrors(false);
    fetchNextNumber(type);
  }, [location.key]);

  // ── الإجماليات ──
  const totals = useMemo(() => {
    const debit = round2(lines.reduce((s, l) => s + num(l.debit), 0));
    const credit = round2(lines.reduce((s, l) => s + num(l.credit), 0));
    const diff = round2(debit - credit);
    return { debit, credit, diff, balanced: Math.abs(diff) < 0.01 && debit > 0 };
  }, [lines]);

  // ── تعديل السطور (بدون تعديل الكائنات القديمة) ──
  const updateLine = (index, field, value) => {
    setLines(prev => {
      const next = prev.map((l, i) => (i === index ? { ...l, [field]: value } : l));
      const row = next[index];
      if (field === 'debit' && num(value) > 0) row.credit = '';
      if (field === 'credit' && num(value) > 0) row.debit = '';

      // عند اختيار حساب لسطر فارغ: نقترح المبلغ الذي يوازن القيد
      if (field === 'accountId' && value && index > 0 && !num(row.debit) && !num(row.credit)) {
        const others = next.filter((_, i) => i !== index);
        const d = round2(others.reduce((s, l) => s + num(l.debit), 0) - others.reduce((s, l) => s + num(l.credit), 0));
        if (d > 0) row.credit = d;
        else if (d < 0) row.debit = Math.abs(d);
      }
      return next;
    });
  };

  const addLine = () => setLines(prev => [...prev, newLine()]);
  const removeLine = (index) => setLines(prev => (prev.length <= 2 ? prev : prev.filter((_, i) => i !== index)));

  // يضع الفرق في أول سطر فارغ (أو سطر جديد)
  const balanceDiff = () => {
    if (!totals.diff) return;
    setLines(prev => {
      const next = [...prev];
      let i = next.findIndex(l => !num(l.debit) && !num(l.credit));
      if (i === -1) { next.push(newLine()); i = next.length - 1; }
      next[i] = totals.diff > 0
        ? { ...next[i], credit: Math.abs(totals.diff), debit: '' }
        : { ...next[i], debit: Math.abs(totals.diff), credit: '' };
      return next;
    });
  };

  // Enter ينتقل للحقل التالي — وفي آخر "دائن" يضيف سطرًا
  const handleKeyDown = (e, index, field) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (field === 'credit' && index === lines.length - 1) { addLine(); return; }
    const els = Array.from(e.target.form?.elements || []);
    const pos = els.indexOf(e.target);
    const nextEl = els.slice(pos + 1).find(el => !el.disabled && el.tagName === 'INPUT');
    if (nextEl) { nextEl.focus(); nextEl.select?.(); }
  };

  // بعد إضافة سطر بـ Enter: التركيز على حساب السطر الجديد
  const prevCount = useRef(lines.length);
  const tableRef = useRef(null);
  useEffect(() => {
    if (lines.length > prevCount.current && tableRef.current) {
      const inputs = tableRef.current.querySelectorAll('tbody tr:last-of-type input');
      inputs[0]?.focus();
    }
    prevCount.current = lines.length;
  }, [lines.length]);

  const handleImportSuccess = (imported) => {
    const mapped = imported.map(l => newLine({
      accountId: l.accountId, description: l.description || '', debit: num(l.debit) || '', credit: num(l.credit) || '',
    }));
    setLines(prev => {
      const empty = prev.every(l => !l.accountId && !num(l.debit) && !num(l.credit));
      return empty ? mapped : [...prev, ...mapped];
    });
  };

  // ── التحقق ──
  const lineError = (l) => {
    if (!l.accountId) return 'اختر الحساب';
    if (!num(l.debit) && !num(l.credit)) return 'أدخل مبلغًا مدينًا أو دائنًا';
    if (num(l.debit) < 0 || num(l.credit) < 0) return 'المبالغ لا تكون سالبة';
    return null;
  };

  const validate = () => {
    const bad = lines.findIndex(l => lineError(l));
    if (bad !== -1) { setShowErrors(true); toast.error(`السطر ${bad + 1}: ${lineError(lines[bad])}`); return false; }
    if (!totals.balanced) { toast.error(`القيد غير متوازن — الفرق ${fmtMoney(Math.abs(totals.diff))}`); return false; }
    if (!form.date) { toast.error('التاريخ مطلوب'); return false; }
    return true;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (isView || saving || !validate()) return;

    const finalDescription = form.description.trim() || `${entryTypeLabel(form.entryType, t)} #${form.entryNumber}`;
    const payload = {
      branchId: form.branchId,
      date: form.date,
      entryType: form.entryType,
      description: finalDescription,
      referenceNumber: form.referenceNumber || null,
      lines: lines.map(l => ({
        accountId: l.accountId,
        description: l.description?.trim() || finalDescription,
        debit: round2(l.debit),
        credit: round2(l.credit),
        costCenterId: l.costCenterId || null,
      })),
    };

    setSaving(true);
    try {
      if (id) {
        await axios.put(`/JournalEntries/${id}`, payload);
        toast.success(`تم تحديث القيد رقم ${form.entryNumber}`);
      } else {
        await axios.post('/JournalEntries', payload);
        toast.success('تم حفظ القيد بنجاح');
      }
      navigate('/journal-entries');
    } catch (err) {
      console.error('Error saving entry:', err);
      toast.error(apiError(err, 'تعذّر حفظ القيد'));
    } finally {
      setSaving(false);
    }
  };

  // Ctrl+S
  const submitRef = useRef(handleSubmit);
  submitRef.current = handleSubmit;
  useEffect(() => {
    if (isView) return undefined;
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') { e.preventDefault(); submitRef.current(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isView]);

  // نسخ القيد إلى قيد جديد (الحالة تنتقل مع التوجيه)
  const handleDuplicate = () => {
    navigate(`/journal-entries/new?type=${form.entryType}`, {
      state: {
        duplicate: {
          entryType: form.entryType,
          description: form.description ? `${form.description} (نسخة)` : '',
          lines: lines.map(({ accountId, description, debit, credit }) => ({ accountId, description, debit, credit })),
        },
      },
    });
  };

  // ── الطباعة ──
  const printRef = useRef(null);
  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `قيد-${form.entryNumber || 'جديد'}`,
  });

  // ── الترويسة ──
  const typeLabel = entryTypeLabel(form.entryType, t);
  const status = ENTRY_STATUS[form.status];
  const modeBadge = isView
    ? { label: locked && !routeView ? 'للعرض فقط' : 'عرض', icon: <VisibilityOutlinedIcon /> }
    : isEdit ? { label: 'تعديل', icon: <EditOutlinedIcon /> }
    : { label: duplicate ? 'نسخة جديدة' : 'قيد جديد', icon: <AddCircleOutlineIcon /> };

  const actions = [
    ...(id && !isSystemEntry(form.entryType)
      ? [{ label: 'نسخ القيد', icon: <ContentCopyIcon />, onClick: handleDuplicate }] : []),
    ...(!isView ? [{ label: 'استيراد Excel', icon: <UploadFileOutlinedIcon />, onClick: () => setImportOpen(true) }] : []),
    { label: 'طباعة', icon: <PrintOutlinedIcon />, onClick: handlePrint, disabled: loading || !!loadError },
    { label: 'رجوع للقائمة', icon: <ArrowForwardIcon />, onClick: () => navigate('/journal-entries') },
  ];

  const ro = isView || saving;

  return (
    <ReportPage
      title={isView ? `عرض ${typeLabel}` : isEdit ? `تعديل ${typeLabel}` : `${typeLabel} جديد`}
      subtitle="مجموع المدين يجب أن يساوي مجموع الدائن"
      icon={<MenuBookOutlinedIcon />}
      badges={[
        { label: form.entryNumber ? `رقم ${form.entryNumber}` : 'رقم تلقائي', icon: <TagIcon /> },
        modeBadge,
        ...(status ? [{ label: status.label }] : []),
      ]}
      actions={actions}
    >
      <Box sx={{ display: 'none' }}>
        <JournalEntryPrint
          ref={printRef}
          data={{
            ...form,
            typeLabel,
            statusLabel: status?.label,
            lines: lines.map(l => ({ ...l, accountName: accountMap[l.accountId] || '' })),
          }}
          companyInfo={companyInfo}
        />
      </Box>

      {loading ? (
        <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 280 }}><CircularProgress /></Box>
      ) : loadError ? (
        <Alert severity="error" sx={{ borderRadius: '12px' }}>{loadError}</Alert>
      ) : (
        <Box component="form" noValidate onSubmit={handleSubmit} sx={{ display: 'grid', gap: 1.5 }}>
          {locked && !routeView && (
            <Alert severity="info" icon={<LockOutlinedIcon />} sx={{ borderRadius: '12px' }}>
              {isSystemEntry(form.entryType)
                ? 'هذا قيد آلي — يُعدّل من العملية الأصلية (الفاتورة أو السند).'
                : 'القيد مرحّل ولا يمكن تعديله. يمكنك نسخه إلى قيد جديد.'}
            </Alert>
          )}

          {/* ═════ بيانات القيد ═════ */}
          <Section title="بيانات القيد" icon={<EventNoteOutlinedIcon />}>
            <Box sx={{
              display: 'grid', gap: 1.5,
              gridTemplateColumns: { xs: 'repeat(2, minmax(0,1fr))', md: 'repeat(4, minmax(0,1fr))' },
            }}>
              <TextField
                size="small" fullWidth label="رقم القيد" disabled
                value={form.entryNumber || 'تلقائي'}
                InputProps={{ startAdornment: <LockOutlinedIcon sx={{ fontSize: 17, color: '#94a3b8', ml: .75 }} /> }}
                sx={fieldSx}
              />
              <TextField
                size="small" fullWidth type="date" label="التاريخ" required
                value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                InputLabelProps={{ shrink: true }} disabled={ro}
                sx={{ ...fieldSx, '& input': { direction: 'ltr', textAlign: 'right' } }}
              />
              <TextField select size="small" fullWidth label="نوع القيد" value={form.entryType} disabled sx={fieldSx}>
                {entryTypes.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </TextField>
              <TextField
                size="small" fullWidth label="الرقم المرجعي" placeholder="اختياري"
                value={form.referenceNumber} onChange={e => setForm(f => ({ ...f, referenceNumber: e.target.value }))}
                disabled={ro} sx={fieldSx}
              />
              <TextField
                size="small" fullWidth multiline minRows={1} maxRows={4}
                label="البيان العام"
                placeholder={`إن تُرك فارغًا: ${typeLabel} #${form.entryNumber || '—'}`}
                value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                disabled={ro}
                sx={{ ...fieldSx, gridColumn: '1 / -1' }}
              />
            </Box>
          </Section>

          {/* ═════ سطور القيد ═════ */}
          <Section
            title={`سطور القيد (${lines.length})`}
            icon={<ListAltOutlinedIcon />}
            noPad
            action={!isView && (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Tooltip title={totals.diff ? 'يضع الفرق في أول سطر فارغ' : 'القيد متوازن'}>
                  <span>
                    <Button
                      size="small" variant="outlined" startIcon={<BalanceIcon />} onClick={balanceDiff}
                      disabled={!totals.diff || ro}
                      sx={{ borderRadius: '10px', fontWeight: 700, '& .MuiButton-startIcon': { ml: .6, mr: -.25 } }}
                    >
                      موازنة الفرق
                    </Button>
                  </span>
                </Tooltip>
                <Button
                  size="small" variant="contained" disableElevation startIcon={<AddIcon />} onClick={addLine} disabled={ro}
                  sx={{ borderRadius: '10px', fontWeight: 700, bgcolor: ACCENT, '& .MuiButton-startIcon': { ml: .6, mr: -.25 } }}
                >
                  سطر
                </Button>
              </Box>
            )}
          >
            <Box ref={tableRef} sx={{ overflowX: 'auto' }}>
              <Box component="table" sx={{
                width: '100%', minWidth: 760, borderCollapse: 'collapse',
                '& th': {
                  bgcolor: '#f8fafc', color: '#334155', fontWeight: 800, fontSize: 13, textAlign: 'right',
                  px: 1, py: 1, borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}`, whiteSpace: 'nowrap',
                },
                '& td': { px: 1, py: .75, borderBottom: '1px solid #f1f5f9', verticalAlign: 'top' },
                '& tbody tr:hover td': { bgcolor: '#fafbff' },
              }}>
                <thead>
                  <tr>
                    <th style={{ width: 40, textAlign: 'center' }}>#</th>
                    <th style={{ width: '34%' }}>الحساب</th>
                    <th style={{ width: 140 }}>مدين</th>
                    <th style={{ width: 140 }}>دائن</th>
                    <th>بيان السطر</th>
                    {!isView && <th style={{ width: 48 }} />}
                  </tr>
                </thead>
                <tbody>
                  {lines.map((line, index) => {
                    const err = showErrors ? lineError(line) : null;
                    return (
                      <tr key={line.id}>
                        <td style={{ textAlign: 'center', paddingTop: 14 }}>
                          <Typography sx={{ fontSize: 13, fontWeight: 700, color: err ? '#e11d48' : '#94a3b8' }}>{index + 1}</Typography>
                        </td>
                        <td>
                          <AccountSelect
                            value={line.accountId}
                            onChange={(val) => updateLine(index, 'accountId', val)}
                            required size="small" disabled={ro}
                            error={showErrors && !line.accountId}
                          />
                        </td>
                        <td>
                          <TextField
                            fullWidth size="small" type="number" placeholder="0.00"
                            value={line.debit}
                            onChange={e => updateLine(index, 'debit', e.target.value)}
                            onKeyDown={e => handleKeyDown(e, index, 'debit')}
                            onFocus={e => e.target.select()}
                            onWheel={e => e.target.blur()}
                            inputProps={{ min: 0, step: '0.01', inputMode: 'decimal' }}
                            disabled={ro}
                            error={!!err && !num(line.debit) && !num(line.credit)}
                            sx={{ ...amountSx, '& input': { ...amountSx['& input'], color: num(line.debit) ? '#0369a1' : undefined } }}
                          />
                        </td>
                        <td>
                          <TextField
                            fullWidth size="small" type="number" placeholder="0.00"
                            value={line.credit}
                            onChange={e => updateLine(index, 'credit', e.target.value)}
                            onKeyDown={e => handleKeyDown(e, index, 'credit')}
                            onFocus={e => e.target.select()}
                            onWheel={e => e.target.blur()}
                            inputProps={{ min: 0, step: '0.01', inputMode: 'decimal' }}
                            disabled={ro}
                            error={!!err && !num(line.debit) && !num(line.credit)}
                            sx={{ ...amountSx, '& input': { ...amountSx['& input'], color: num(line.credit) ? '#047857' : undefined } }}
                          />
                        </td>
                        <td>
                          <TextField
                            fullWidth size="small" placeholder="يُستخدم البيان العام إن تُرك فارغًا"
                            value={line.description}
                            onChange={e => updateLine(index, 'description', e.target.value)}
                            onKeyDown={e => handleKeyDown(e, index, 'description')}
                            disabled={ro} sx={fieldSx}
                          />
                        </td>
                        {!isView && (
                          <td style={{ textAlign: 'center', paddingTop: 10 }}>
                            <Tooltip title={lines.length <= 2 ? 'القيد يحتاج سطرين على الأقل' : 'حذف السطر'}>
                              <span>
                                <IconButton size="small" onClick={() => removeLine(index)} disabled={lines.length <= 2 || ro}
                                  sx={{ color: '#e11d48', '&.Mui-disabled': { opacity: .3 } }}>
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <td />
                    <td style={{ fontWeight: 800, fontSize: 14, paddingTop: 12, paddingBottom: 12 }}>الإجمالي</td>
                    <td style={{ fontWeight: 800, fontSize: 15, direction: 'ltr', textAlign: 'right', color: '#0369a1' }}>{fmtMoney(totals.debit)}</td>
                    <td style={{ fontWeight: 800, fontSize: 15, direction: 'ltr', textAlign: 'right', color: '#047857' }}>{fmtMoney(totals.credit)}</td>
                    <td colSpan={isView ? 1 : 2} />
                  </tr>
                </tfoot>
              </Box>
            </Box>
            {!isView && (
              <Typography sx={{ px: 2, py: 1, fontSize: 12, color: '#94a3b8', borderTop: `1px solid ${BORDER}` }}>
                Enter للانتقال بين الحقول · Enter في آخر خانة "دائن" يضيف سطرًا · Ctrl+S للحفظ
              </Typography>
            )}
          </Section>

          {/* ═════ شريط التوازن والحفظ ═════ */}
          <Paper elevation={0} sx={{
            position: 'sticky', bottom: 8, zIndex: 5,
            display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, flexWrap: 'wrap',
            px: { xs: 1.5, sm: 2 }, py: 1.25, borderRadius: '14px',
            border: `1px solid ${totals.balanced ? '#a7f3d0' : '#fecdd3'}`,
            bgcolor: '#fff', boxShadow: '0 -8px 24px -18px rgba(15,23,42,.45)',
          }}>
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: .75, px: 1.25, py: .6, borderRadius: '10px',
              bgcolor: totals.balanced ? '#d1fae5' : '#ffe4e6', color: totals.balanced ? '#047857' : '#be123c',
              fontWeight: 800, fontSize: 14,
            }}>
              {totals.balanced ? <CheckCircleOutlineIcon fontSize="small" /> : <ErrorOutlineIcon fontSize="small" />}
              {totals.balanced ? 'القيد متوازن' : totals.debit === 0 && totals.credit === 0 ? 'أدخل المبالغ' : `الفرق ${fmtMoney(Math.abs(totals.diff))}`}
            </Box>
            <Box sx={{ display: 'flex', gap: 2.5, flexWrap: 'wrap' }}>
              {[['مدين', totals.debit, '#0369a1'], ['دائن', totals.credit, '#047857']].map(([k, v, c]) => (
                <Box key={k} sx={{ display: 'flex', alignItems: 'baseline', gap: .75 }}>
                  <Typography sx={{ fontSize: 13, color: '#64748b' }}>{k}</Typography>
                  <Typography sx={{ fontSize: 16, fontWeight: 800, color: c, direction: 'ltr' }}>{fmtMoney(v)}</Typography>
                </Box>
              ))}
            </Box>
            <Box sx={{ flex: 1 }} />
            <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', sm: 'auto' } }}>
              <Button
                variant="outlined" color="inherit" onClick={() => navigate('/journal-entries')} disabled={saving}
                sx={{ flex: { xs: 1, sm: 'none' }, borderRadius: '11px', fontWeight: 700, borderColor: BORDER }}
              >
                {isView ? 'رجوع' : 'إلغاء'}
              </Button>
              {!isView && (
                <Button
                  type="submit" variant="contained" disableElevation
                  disabled={saving || !totals.balanced}
                  startIcon={saving ? <CircularProgress size={17} color="inherit" /> : <SaveOutlinedIcon />}
                  sx={{
                    flex: { xs: 2, sm: 'none' }, minWidth: 140, borderRadius: '11px', fontWeight: 800,
                    bgcolor: ACCENT, '& .MuiButton-startIcon': { ml: .75, mr: -.25 },
                  }}
                >
                  {saving ? 'جاري الحفظ…' : id ? 'تحديث القيد' : 'حفظ القيد'}
                </Button>
              )}
            </Box>
          </Paper>
        </Box>
      )}

      <ImportJournalEntryDialog open={importOpen} onClose={() => setImportOpen(false)} onImport={handleImportSuccess} />
    </ReportPage>
  );
}
